import type { TypedNativeModules } from '../native-modules/types/NativeModulesTypes';

import { getRNAppBundleVersion } from './LoggableDeviceProperties';

import { NativeModules } from 'react-native';

const NativeModulesTyped = NativeModules as TypedNativeModules;
const bridge = NativeModulesTyped.RNDatadogBridge;

/**
 * Enriches context with rnBundleVersion for all Datadog logs.
 */
const enrichContext = (context?: Record<string, unknown>): Record<string, unknown> => {
    const rnBundleVersion = getRNAppBundleVersion();
    return { ...context, rnBundleVersion };
};

/**
 * DatadogLogger provides methods to send logs and RUM events to Datadog via the native bridge.
 * This logger uses the shared native Datadog SDK session, avoiding conflicts with native logging.
 *
 * @example Basic logging
 * ```typescript
 * import { DatadogLogger } from '@core/logging/DatadogLogger';
 *
 * // Simple logs
 * await DatadogLogger.info('User logged in successfully');
 * await DatadogLogger.debug('Cache hit for user profile', { userId: '123', cacheKey: 'profile_123' });
 *
 * // Log with additional context
 * await DatadogLogger.warn('API rate limit approaching', {
 *   endpoint: '/api/users',
 *   requestCount: 95,
 *   limit: 100
 * });
 * ```
 *
 * @example Error logging with stack traces
 * ```typescript
 * try {
 *   await fetchData();
 * } catch (error) {
 *   await DatadogLogger.error(
 *     'Failed to fetch user data',
 *     { userId: '123', endpoint: '/api/user' },
 *     error.name,           // e.g., 'TypeError'
 *     error.message,        // e.g., 'Cannot read property of undefined'
 *     error.stack           // Full stack trace
 *   );
 * }
 * ```
 *
 * @example RUM view tracking (automatically handled by logScreenView)
 * ```typescript
 * // Typically you don't need to call these directly - use logScreenView instead
 * await DatadogLogger.startView('profile_screen', 'User Profile', { userId: '123' });
 * // ... user interacts with screen ...
 * await DatadogLogger.stopView('profile_screen');
 * ```
 *
 * Note: For most error logging, you should use `console.error()` which is automatically
 * intercepted by GlobalErrorHandler and sent to Datadog with proper formatting.
 *
 * @example Using console.error (RECOMMENDED for most cases)
 * ```typescript
 * // This is automatically captured and sent to Datadog:
 * console.error('Failed to load data', new Error('Network timeout'));
 *
 * // The GlobalErrorHandler will:
 * // 1. Extract error name, message, and stack trace
 * // 2. Send to Datadog RUM as an error event
 * // 3. Send to Datadog Logs with error context
 * // 4. Call the original console.error for debugging
 * ```
 */
export const DatadogLogger = {
    /**
     * Log a debug message to Datadog.
     * Use for detailed diagnostic information useful during development.
     *
     * @param message - The log message (e.g., 'Cache hit for user profile')
     * @param context - Optional key-value pairs for additional context (e.g., { userId: '123', cacheKey: 'profile_123' })
     *
     * @example
     * await DatadogLogger.debug('User cache accessed', { userId: '123', hit: true });
     */
    debug: async (message: string, context?: Record<string, unknown>): Promise<void> => {
        if (bridge?.logDebug) {
            await bridge.logDebug(message, enrichContext(context));
        }
    },

    /**
     * Log an informational message to Datadog.
     * Use for general informational messages about application flow.
     *
     * @param message - The log message (e.g., 'User logged in successfully')
     * @param context - Optional key-value pairs for additional context (e.g., { userId: '123', loginMethod: 'oauth' })
     *
     * @example
     * await DatadogLogger.info('Job application submitted', { jobId: 'abc123', userId: '456' });
     */
    info: async (message: string, context?: Record<string, unknown>): Promise<void> => {
        if (bridge?.logInfo) {
            await bridge.logInfo(message, enrichContext(context));
        }
    },

    /**
     * Log a warning message to Datadog.
     * Use for potentially harmful situations that don't prevent the application from functioning.
     *
     * @param message - The log message (e.g., 'API rate limit approaching')
     * @param context - Optional key-value pairs for additional context (e.g., { endpoint: '/api/users', count: 95 })
     * @param errorKind - Optional error type (e.g., 'ValidationWarning', 'DeprecationWarning')
     * @param errorMessage - Optional detailed error message
     * @param stacktrace - Optional stack trace string
     *
     * @example Without error details
     * await DatadogLogger.warn('Cache miss - falling back to network', { key: 'user_profile_123' });
     *
     * @example With error details
     * await DatadogLogger.warn(
     *   'Deprecated API usage detected',
     *   { endpoint: '/api/v1/users' },
     *   'DeprecationWarning',
     *   'This endpoint will be removed in v2.0',
     *   new Error().stack
     * );
     */
    warn: async (
        message: string,
        context?: Record<string, unknown>,
        errorKind?: string,
        errorMessage?: string,
        stacktrace?: string,
    ): Promise<void> => {
        if (bridge?.logWarn) {
            await bridge.logWarn(
                message,
                enrichContext(context),
                errorKind,
                errorMessage,
                stacktrace,
            );
        }
    },

    /**
     * Log an error message to Datadog.
     * Use for error conditions that prevent normal operation but don't crash the app.
     *
     * NOTE: For most error logging, prefer using `console.error()` which is automatically
     * intercepted and formatted by GlobalErrorHandler.
     *
     * @param message - The log message (e.g., 'Failed to fetch user data')
     * @param context - Optional key-value pairs for additional context (e.g., { userId: '123', endpoint: '/api/user' })
     * @param errorKind - Optional error type/name (e.g., 'TypeError', 'NetworkError', 'ValidationError')
     * @param errorMessage - Optional detailed error message (e.g., 'Cannot read property "name" of undefined')
     * @param stacktrace - Optional full stack trace string
     *
     * @example Simple error
     * await DatadogLogger.error('API request failed', { endpoint: '/api/jobs', statusCode: 500 });
     *
     * @example With full error details
     * try {
     *   await api.fetchUser(userId);
     * } catch (error) {
     *   await DatadogLogger.error(
     *     'Failed to fetch user data',
     *     { userId, endpoint: '/api/user' },
     *     error.name,           // 'TypeError'
     *     error.message,        // 'Cannot read property of undefined'
     *     error.stack           // Full stack trace
     *   );
     * }
     *
     * @example RECOMMENDED: Use console.error instead (automatically captured)
     * try {
     *   await api.fetchUser(userId);
     * } catch (error) {
     *   // This is automatically sent to Datadog with proper formatting:
     *   console.error('Failed to fetch user data', error);
     * }
     */
    error: async (
        message: string,
        context?: Record<string, unknown>,
        errorKind?: string,
        errorMessage?: string,
        stacktrace?: string,
    ): Promise<void> => {
        if (bridge?.logError) {
            await bridge.logError(
                message,
                enrichContext(context),
                errorKind,
                errorMessage,
                stacktrace,
            );
        }
    },

    /**
     * Start tracking a RUM (Real User Monitoring) view in Datadog.
     * This creates a new view event in the RUM session.
     *
     * NOTE: This is typically called automatically by `logScreenView()` and `logScreenViewWithExtraParams()`.
     * You generally don't need to call this directly.
     *
     * @param key - Unique identifier for the view (e.g., 'profile_screen', 'job_details_abc123')
     * @param name - Human-readable view name (e.g., 'User Profile', 'Job Details')
     * @param context - Optional key-value pairs for additional context (e.g., { userId: '123', jobId: 'abc123' })
     *
     * @example
     * await DatadogLogger.startView('job_search_results', 'Job Search Results', {
     *   searchQuery: 'software engineer',
     *   resultCount: 50,
     *   location: 'San Francisco'
     * });
     */
    startView: async (
        key: string,
        name?: string,
        context?: Record<string, unknown>,
    ): Promise<void> => {
        if (bridge?.startView) {
            await bridge.startView(key, name, enrichContext(context));
        }
    },

    /**
     * Stop tracking a RUM view in Datadog.
     * This ends the view event started by `startView()`.
     *
     * NOTE: This is typically managed automatically by navigation events.
     * You generally don't need to call this directly.
     *
     * @param key - The same unique identifier used in startView (e.g., 'profile_screen')
     * @param context - Optional key-value pairs for additional context at view end
     *
     * @example
     * await DatadogLogger.stopView('job_search_results', {
     *   timeSpent: 45000,
     *   itemsViewed: 10
     * });
     */
    stopView: async (key: string, context?: Record<string, unknown>): Promise<void> => {
        if (bridge?.stopView) {
            await bridge.stopView(key, enrichContext(context));
        }
    },

    /**
     * Add an error event to Datadog RUM (Real User Monitoring).
     * This creates a RUM error event that appears in the RUM dashboard.
     *
     * NOTE: This is typically called automatically by GlobalErrorHandler.
     * You generally don't need to call this directly - use `console.error()` instead.
     *
     * @param message - The error message (e.g., 'Network request failed')
     * @param source - Error source type ('source', 'network', 'console', 'webview', 'custom')
     * @param stacktrace - Full stack trace string
     * @param context - Optional key-value pairs for additional context
     *
     * @example
     * await DatadogLogger.addRumError(
     *   'Failed to load job details',
     *   'network',
     *   error.stack || '',
     *   {
     *     jobId: 'abc123',
     *     statusCode: 500,
     *     endpoint: '/api/jobs/abc123'
     *   }
     * );
     */
    addRumError: async (
        message: string,
        source: string,
        stacktrace: string,
        context?: Record<string, unknown>,
    ): Promise<void> => {
        if (bridge?.addError) {
            await bridge.addError(message, source, stacktrace, enrichContext(context));
        }
    },
};
